<template>
  <div :class="classMessageType">
    <div
      class="message__bubble"
      :class="{ date: hideTimestamp, transfer: isTransfer }"
    >
      <div class="message__notification">
        <div
          v-if="showIcon"
          class="message__notification-icon"
          :class="classStatus()"
        >
          <component :is="iconComponent" />
        </div>
        <span :class="classTextContent" v-html="notificationText"></span>
        <div v-if="!hideTimestamp" class="message__notification-trail">
          {{ timestamp }}
        </div>
      </div>
    </div>
    <div
      class="message__notification-separator"
      :class="{ 'separator-blue': hideTimestamp || isTransfer }"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import LoadingArrowIcon from '@/assets/icons/loading-arrow.svg';
import ShuffleIcon from '@/assets/icons/shuffle.svg';

export default {
  components: {
    LoadingArrowIcon,
    ShuffleIcon,
  },
  props: {
    message: {
      required: true,
      type: Object,
    },
    hasNext: {
      default: false,
    },
  },
  computed: {
    ...mapGetters([
      'theme',
    ]),
    classMessageType() {
      return [
        'message',
        'message--item',

        {
          'message--has-next': this.hasNext,
        },
      ];
    },
    classTextContent() {
      return [
        'message__notification-content',
        {
          'message__notification-content--uppercase': this.hasRelativeTimestamp,
          date: this.hideTimestamp,
        },
      ];
    },
    timestamp() {
      if (this.message.createdAt) {
        return this.globalFormatTime(this.message.createdAt);
      }

      return null;
    },
    notificationType() {
      return this.message.type ?? null;
    },
    isStatusChange() {
      return this.notificationType === 'statusChange';
    },
    statusChange() {
      return this.message.statusChange ?? {};
    },
    statusTo() {
      return this.statusChange.to ?? 0;
    },
    statusLabel() {
      return `<span style="color:${this.statusColor()};">${this.$t('chat.conversation')}
      <b>${this.getStatusFromId(this.statusTo)}</b></span>`;
    },
    isTransfer() {
      return this.notificationType === 'transfer';
    },
    transfer() {
      return this.message.transfer ?? {};
    },
    transferLabel() {
      // TODO implement
      if (this.transfer.from && this.transfer.to) {
        return this.$t('chat.transfer', {
          to: this.transfer.to.name,
          from: this.transfer.from.name,
        });
      }
      return '';
    },
    showIcon() {
      return this.isStatusChange || this.isTransfer;
    },
    iconComponent() {
      if (this.isStatusChange) {
        return 'loading-arrow-icon';
      }

      if (this.isTransfer) {
        return 'shuffle-icon';
      }

      return '';
    },
    notificationText() {
      if (this.isStatusChange) {
        return this.statusLabel;
      }

      if (this.isTransfer) {
        return this.transferLabel;
      }

      if (this.hasRelativeTimestamp) {
        return this.globalFormatRelativeDateTime(this.message.relativeTimestamp, false, true);
      }

      if (this.message.text) {
        return this.message.text;
      }

      return '';
    },
    hasRelativeTimestamp() {
      return !!this.message.relativeTimestamp;
    },
    hideTimestamp() {
      return !!this.message.hideTimestamp;
    },
  },
  methods: {
    getStatusFromId(id) {
      return this.translateStatus(id);
    },
    classStatus() {
      switch (this.statusTo) {
        case 1:
          return 'not-assigned';
        case 2:
          return 'assigned';
        case 3:
          return 'in-progress';
        case 4:
          return 'resolved';
        case 5:
          return 'cancelled';
        case 6:
          return 'out-of-time';
        case 7:
          return 'disconnected-agents';
        case 8:
          return 'ivr';
        default:
          return '';
      }
    },
    statusColor() {
      switch (this.statusTo) {
        case 1:
          return '';
        case 2:
          return '#cca300';
        case 3:
          return '#37ae5f';
        case 4:
          return this.theme === 'light' ? '#020094' : '#3674ea';
        case 5:
          return '#c20000';
        case 6:
          return '#a9a6a9';
        case 7:
          return '#49657B';
        case 8:
          return '#03a9f4';
        default:
          return '';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~styles/components/message-status.scss";
</style>
